import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { User, AlertCircle, Users, Calendar, Download, Check, X, SlidersHorizontal,Eye, FileText, Briefcase, MapPin, Clock, Wallet } from 'lucide-react';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';
import { useTranslation } from 'react-i18next';
import {
    getZeroLoginDoctors,
    getPendingProfileDoctors,
    getZeroPatientsDoctors,
    getZeroAppointmentsDoctors,
    getLessThanTenAppointmentsDoctors,
    downloadZeroLoginExcel,
    downloadPendingProfileExcel,
    downloadZeroPatientsExcel,
    downloadZeroAppointmentsExcel,
    downloadLessThanTenAppointmentsExcel,
    getDoctorStatusCounts,
} from '../../redux/reportsSlice';
import { getDoctorById } from '../../redux/doctorSlice';
import { getCountries } from '../../redux/masterSlice';
import { toast } from 'react-toastify';
import DoctorProfileModal from '../../components/modals/DoctorProfileModal';
import { FilterPanel, FilterSection, FilterCountryList, FilterOptionList, FilterPopover } from '../../components/common/FilterPanel';
import MasterLabel from '../../components/common/MasterLabel';

const ReportsDoctorStatus = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        zeroLoginDoctors,
        pendingProfileDoctors,
        zeroPatientsDoctors,
        zeroAppointmentsDoctors,
        lessThanTenAppointmentsDoctors,
        doctorStatusCounts,
        loading,
        downloadLoading,
        countsLoading,
    } = useSelector((state) => state.reports);
    const { selectedDoctor, loading: doctorLoading } = useSelector((state) => state.doctor);
    const { countries } = useSelector((state) => state.master);

    const [selectedCard, setSelectedCard] = useState('zero-login');
    const [pendingData, setPendingData] = useState(''); // Track pending_data parameter
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState({ gender: [], countryId: [] });
    const [filters, setFilters] = useState({ gender: [], countryId: [] });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const filterButtonRef = useRef(null);
    const filterRef = useRef(null);

    // Get current data based on selected card
    const getCurrentData = () => {
        switch (selectedCard) {
            case 'zero-login':
                return zeroLoginDoctors?.data || [];
            case 'profile-pending-detail':
            case 'speciality-pending':
            case 'address-pending':
            case 'availability-pending':
            case 'payment-bank-pending':
                return pendingProfileDoctors?.data || [];
            // case 'zero-patients':
            //     return zeroPatientsDoctors?.data || [];
            case 'zero-appointments':
                return zeroAppointmentsDoctors?.data || [];
            case 'less-than-10-appointments':
                return lessThanTenAppointmentsDoctors?.data || [];
            default:
                return [];
        }
    };

    // Get total items based on selected card
    const getTotalItems = () => {
        switch (selectedCard) {
            case 'zero-login':
                return zeroLoginDoctors?.totalItems || 0;
            case 'profile-pending-detail':
            case 'speciality-pending':
            case 'address-pending':
            case 'availability-pending':
            case 'payment-bank-pending':
                return pendingProfileDoctors?.totalItems || 0;
            // case 'zero-patients':
            //     return zeroPatientsDoctors?.totalItems || 0;
            case 'zero-appointments':
                return zeroAppointmentsDoctors?.totalItems || 0;
            case 'less-than-10-appointments':
                return lessThanTenAppointmentsDoctors?.totalItems || 0;
            default:
                return 0;
        }
    };

    const currentDoctors = getCurrentData();
    const totalItems = getTotalItems();
    const indexOfFirstDoctor = (currentPage - 1) * itemsPerPage;

    // Fetch countries on mount
    useEffect(() => {
        dispatch(getCountries());
    }, [dispatch]);

    // Sync tempFilters with filters when popup opens
    useEffect(() => {
        if (isFilterOpen) {
            setTempFilters({ ...filters });
        }
    }, [isFilterOpen]);

    // Close filter on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target) &&
                filterButtonRef.current &&
                !filterButtonRef.current.contains(event.target)
            ) {
                // Reset tempFilters to current filters when closing without applying
                setTempFilters({ ...filters });
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filters]);

    // Fetch data based on selected card
    const fetchData = (page = 1) => {
        const params = { page, limit: itemsPerPage };
        if (filters.gender.length > 0) params.gender = filters.gender.join(',');
        if (filters.countryId.length > 0) params.countryId = filters.countryId.join(',');

        switch (selectedCard) {
            case 'zero-login':
                dispatch(getZeroLoginDoctors(params));
                break;
            case 'profile-pending-detail':
            case 'speciality-pending':
            case 'address-pending':
            case 'availability-pending':
            case 'payment-bank-pending':
                if (pendingData) {
                    params.pending_data = pendingData;
                }
                dispatch(getPendingProfileDoctors(params));
                break;
            // case 'zero-patients':
            //     dispatch(getZeroPatientsDoctors(params));
            //     break;
            case 'zero-appointments':
                dispatch(getZeroAppointmentsDoctors(params));
                break;
            case 'less-than-10-appointments':
                dispatch(getLessThanTenAppointmentsDoctors(params));
                break;
            default:
                break;
        }
    };

    // Fetch counts on mount and when filters change
    useEffect(() => {
        const params = {};
        if (filters.gender.length > 0) params.gender = filters.gender.join(',');
        if (filters.countryId.length > 0) params.countryId = filters.countryId.join(',');
        dispatch(getDoctorStatusCounts(params));
    }, [dispatch, filters]);

    // Fetch data when card changes or on mount
    useEffect(() => {
        fetchData(1);
        setCurrentPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCard, pendingData]);

    // Fetch data when filters change
    useEffect(() => {
        fetchData(1);
        setCurrentPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const handlePageChange = (page, newLimit) => {
        setCurrentPage(page);

        if (newLimit && newLimit !== itemsPerPage) {
            setItemsPerPage(newLimit);
        }

        const limit = newLimit || itemsPerPage;
        const params = { page, limit };
        if (filters.gender.length > 0) params.gender = filters.gender.join(',');
        if (filters.countryId.length > 0) params.countryId = filters.countryId.join(',');

        switch (selectedCard) {
            case 'zero-login':
                dispatch(getZeroLoginDoctors(params));
                break;
            case 'profile-pending-detail':
            case 'speciality-pending':
            case 'address-pending':
            case 'availability-pending':
            case 'payment-bank-pending':
                if (pendingData) {
                    params.pending_data = pendingData;
                }
                dispatch(getPendingProfileDoctors(params));
                break;
            // case 'zero-patients':
            //     dispatch(getZeroPatientsDoctors(params));
            //     break;
            case 'zero-appointments':
                dispatch(getZeroAppointmentsDoctors(params));
                break;
            case 'less-than-10-appointments':
                dispatch(getLessThanTenAppointmentsDoctors(params));
                break;
            default:
                break;
        }
    };

    // Download Excel
    const handleDownload = async () => {
        try {
            let resultAction;
            const downloadParams = {};
            if (filters.gender.length > 0) downloadParams.gender = filters.gender.join(',');
            if (filters.countryId.length > 0) downloadParams.countryId = filters.countryId.join(',');

            switch (selectedCard) {
                case 'zero-login':
                    resultAction = await dispatch(downloadZeroLoginExcel(downloadParams));
                    break;
                case 'profile-pending-detail':
                case 'speciality-pending':
                case 'address-pending':
                case 'availability-pending':
                case 'payment-bank-pending':
                    if (pendingData) {
                        downloadParams.pending_data = pendingData;
                    }
                    resultAction = await dispatch(downloadPendingProfileExcel(downloadParams));
                    break;
                // case 'zero-patients':
                //     resultAction = await dispatch(downloadZeroPatientsExcel(downloadParams));
                //     break;
                case 'zero-appointments':
                    resultAction = await dispatch(downloadZeroAppointmentsExcel(downloadParams));
                    break;
                case 'less-than-10-appointments':
                    resultAction = await dispatch(downloadLessThanTenAppointmentsExcel(downloadParams));
                    break;
                default:
                    return;
            }

            if (resultAction.type.endsWith('/fulfilled')) {
                const blob = resultAction.payload;
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${selectedCard}-report.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success(t('reportsDoctorStatus.excel_download_success'));
            } else {
                toast.warning(t('reportsDoctorStatus.excel_download_failed'));
            }
        } catch (error) {
            toast.warning(t('reportsDoctorStatus.excel_download_error'));
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // Handle view doctor details
    const handleViewDoctor = async (doctor) => {
        const doctorId = doctor.doctorId || doctor.id;

        if (!doctorId) {
            toast.warning(t('reportsDoctorStatus.doctor_id_not_found'));
            return;
        }

        setIsModalOpen(true);
        try {
            await dispatch(getDoctorById(doctorId)).unwrap();
        } catch (error) {
            console.error('Error fetching doctor details:', error);
            toast.warning(t('reportsDoctorStatus.failed_load_doctor'));
            setIsModalOpen(false);
        }
    };

    // Card definitions - 7 cards in one array (removed profile-pending)
    const statusCards = [
        { id: 'zero-login', label: t('reportsDoctorStatus.zero_login'), icon: User, color: 'from-cyan-500 to-blue-600' },
        { id: 'profile-pending-detail', label: t('reportsDoctorStatus.profile_pending'), icon: FileText, color: 'from-blue-500 to-blue-600' },
        { id: 'speciality-pending', label: t('reportsDoctorStatus.speciality_pending'), icon: Briefcase, color: 'from-purple-500 to-purple-600' },
        { id: 'address-pending', label: t('reportsDoctorStatus.address_pending'), icon: MapPin, color: 'from-green-500 to-green-600' },
        { id: 'availability-pending', label: t('reportsDoctorStatus.availability_pending'), icon: Clock, color: 'from-orange-500 to-orange-600' },
        { id: 'payment-bank-pending', label: t('reportsDoctorStatus.payment_pending', 'Payment & Bank Pending'), icon: Wallet, color: 'from-teal-500 to-emerald-600' },
        { id: 'zero-appointments', label: t('reportsDoctorStatus.zero_appointments'), icon: Calendar, color: 'from-purple-500 to-indigo-600' },
        { id: 'less-than-10-appointments', label: t('reportsDoctorStatus.less_than_10'), icon: Calendar, color: 'from-amber-500 to-orange-600' },
    ];

    const getCount = (id) => {
        if (!doctorStatusCounts) return 0;

        switch (id) {
            case 'zero-login':
                return doctorStatusCounts?.newRegistration || 0;
            // case 'zero-patients':
            //     return doctorStatusCounts?.zeroPatients || 0;
            case 'zero-appointments':
                return doctorStatusCounts?.zeroAppointments || 0;
            case 'less-than-10-appointments':
                return doctorStatusCounts?.lessThanTen || 0;
            case 'profile-pending-detail':
                return doctorStatusCounts?.ProfilePending || 0;
            case 'speciality-pending':
                return doctorStatusCounts?.SpecialityPending || 0;
            case 'address-pending':
                return doctorStatusCounts?.AddressPending || 0;
            case 'availability-pending':
                return doctorStatusCounts?.AvailabilityPending || 0;
            case 'payment-bank-pending':
                return doctorStatusCounts?.PaymentPending || 0;
            default:
                return 0;
        }
    };

    // Check if selected card is a pending profile card
    const isPendingProfileCard = () => {
        return selectedCard === 'profile-pending-detail' ||
            selectedCard === 'speciality-pending' ||
            selectedCard === 'address-pending' ||
            selectedCard === 'availability-pending' ||
            selectedCard === 'payment-bank-pending';
    };

    // Get table columns based on selected pending card
    const getPendingTableColumns = () => {
        switch (selectedCard) {
            case 'profile-pending-detail':
                return ['profile'];
            case 'speciality-pending':
                return ['profile', 'speciality'];
            case 'address-pending':
                return ['profile', 'speciality', 'address'];
            case 'availability-pending':
                return ['profile', 'speciality', 'address', 'availability'];
            case 'payment-bank-pending':
                return ['profile', 'speciality', 'address', 'availability', 'payment'];
            default:
                return [];
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white w-full rounded-2xl p-4 sm:p-6 shadow-md"
            data-testid="reports-doctor-status-container"
        >
            {/* Header - Same as ReportsDoctors */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4" data-testid="reports-doctor-status-header">
                <h3 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent flex items-center gap-3" data-testid="reports-doctor-status-title">
                    {/* <Stethoscope className="w-8 h-8" /> */}
                    {t('reportsDoctorStatus.title')}
                </h3>

                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto" data-testid="reports-doctor-status-actions-container">
                    {/* Filter Button */}
                    <div className="relative w-full sm:w-auto" data-testid="reports-doctor-status-filter-container">
                        <button
                            ref={filterButtonRef}
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                            className="group flex items-center justify-center gap-2 w-full sm:w-auto h-full px-3 py-2.5 text-white bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                            aria-label={t('reportsDoctorStatus.filter')}
                            aria-expanded={isFilterOpen}
                            data-testid="reports-doctor-status-filter-button"
                        >
                            <SlidersHorizontal className="w-5 h-5" data-testid="reports-doctor-status-filter-icon" />
                            {/* {t('reportsDoctorStatus.filter')} */}
                        </button>

                        {isFilterOpen && (
                            <FilterPopover ref={filterRef}>
                                <FilterPanel
                                    onClear={() => {
                                        setTempFilters({ gender: [], countryId: [] });
                                        setFilters({ gender: [], countryId: [] });
                                        setIsFilterOpen(false);
                                    }}
                                    onApply={() => {
                                        setFilters(tempFilters);
                                        setIsFilterOpen(false);
                                    }}
                                    clearLabel={t('reportsDoctorStatus.filter_clear')}
                                    applyLabel={t('reportsDoctorStatus.filter_apply')}
                                >
                                    <FilterSection title={t('reportsDoctorStatus.filter_gender')} isActive={tempFilters.gender.length > 0}>
                                        <FilterOptionList
                                            multiple
                                            value={tempFilters.gender}
                                            onChange={(val) => setTempFilters({ ...tempFilters, gender: val })}
                                            options={[
                                                { value: 'male', label: t('reportsDoctorStatus.filter_male') },
                                                { value: 'female', label: t('reportsDoctorStatus.filter_female') },
                                                { value: 'Not_Communicated', label: t('reportsDoctorStatus.filter_Not_Communicated') },
                                                { value: 'other', label: t('reportsDoctorStatus.filter_others') },
                                            ]}
                                        />
                                    </FilterSection>

                                    <FilterSection title={t('reportsDoctorStatus.filter_country', 'Country')} isActive={tempFilters.countryId.length > 0}>
                                        <FilterCountryList
                                            multiple
                                            value={tempFilters.countryId}
                                            onChange={(val) => setTempFilters({ ...tempFilters, countryId: val })}
                                            countries={countries}
                                            searchPlaceholder={t('reportsDoctorStatus.search_placeholder', 'Search...')}
                                        />
                                    </FilterSection>
                                </FilterPanel>
                            </FilterPopover>
                        )}
                    </div>

                    {/* Download Button */}
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleDownload}
                        disabled={downloadLoading || currentDoctors.length === 0}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        data-testid="reports-doctor-status-download-button"
                    >
                        <Download className="w-4 h-4" data-testid="reports-doctor-status-download-icon" />
                        {downloadLoading ? t('reportsDoctorStatus.downloading') : t('reportsDoctorStatus.download_excel')}
                    </motion.button>
                </div>
            </div>

            {/* Applied Filters Display */}
            {(filters.gender.length > 0 || filters.countryId.length > 0) && (
                <div className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="reports-doctor-status-applied-filters-container">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700 mr-2" data-testid="reports-doctor-status-applied-filters-label">
                            {t('reportsDoctorStatus.applied_filters')}:
                        </span>

                        {filters.gender.map((g) => (
                            <div key={`gender-${g}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm" data-testid={`reports-doctor-status-applied-gender-chip-${g}`}>
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('reportsDoctorStatus.filter_gender')}:</span> {t(`reportsDoctorStatus.filter_${g}`)}
                                </span>
                                <button
                                    onClick={() => setFilters({ ...filters, gender: filters.gender.filter((x) => x !== g) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                    data-testid={`reports-doctor-status-applied-gender-remove-button-${g}`}
                                >
                                    <X className="h-3.5 w-3.5" data-testid={`reports-doctor-status-applied-gender-remove-icon-${g}`} />
                                </button>
                            </div>
                        ))}

                        {filters.countryId.map((cid) => (
                            <div key={`country-${cid}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm" data-testid={`reports-doctor-status-applied-country-chip-${cid}`}>
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('reportsDoctorStatus.filter_country', 'Country')}:</span> <MasterLabel type="masterCountry" id={cid} field="countryName" fallback={countries?.find(c => c.countryId === cid)?.countryName || cid} />
                                </span>
                                <button
                                    onClick={() => setFilters({ ...filters, countryId: filters.countryId.filter((x) => x !== cid) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                    data-testid={`reports-doctor-status-applied-country-remove-button-${cid}`}
                                >
                                    <X className="h-3.5 w-3.5" data-testid={`reports-doctor-status-applied-country-remove-icon-${cid}`} />
                                </button>
                            </div>
                        ))}

                        <button
                            onClick={() => {
                                setFilters({ gender: [], countryId: [] });
                                setTempFilters({ gender: [], countryId: [] });
                            }}
                            className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
                            data-testid="reports-doctor-status-clear-all-button"
                        >
                            <X className="h-3.5 w-3.5" data-testid="reports-doctor-status-clear-all-icon" />
                            {t('reportsDoctorStatus.clear_all')}
                        </button>
                    </div>
                </div>
            )}

            {/* All Status Cards - 7 cards in single row */}
            <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 mb-6" data-testid="reports-doctor-status-cards-container">
                {statusCards.map((card, index) => {
                    const Icon = card.icon;
                    const count = getCount(card.id);
                    const isActive = selectedCard === card.id;
                    const isMainCard = index === 0 || index >= 5;

                    const getPendingDataValue = (cardId) => {
                        const pendingDataMap = {
                            'profile-pending-detail': 'ProfilePending',
                            'speciality-pending': 'SpecialityPending',
                            'address-pending': 'AddressPending',
                            'availability-pending': 'AvailabilityPending',
                            'payment-bank-pending': 'PaymentPending'
                        };
                        return pendingDataMap[cardId] || '';
                    };

                    const handleClick = () => {
                        setSelectedCard(card.id);
                        if (isMainCard) {
                            setPendingData('');
                        } else {
                            setPendingData(getPendingDataValue(card.id));
                        }
                        setCurrentPage(1);
                    };

                    return (
                        <motion.button
                            key={card.id}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleClick}
                            disabled={countsLoading}
                            className={`relative rounded-lg p-2.5 transition-all duration-300 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed z-10 ${isActive
                                ? 'text-white shadow-lg'
                                : 'bg-gray-50 border border-gray-200 hover:bg-gray-100 text-gray-700'
                                }`}
                            data-testid={`reports-doctor-status-card-${card.id}`}
                        >
                            {isActive && (
                                <motion.div
                                    layoutId="activeDoctorStatusCard"
                                    className="absolute inset-0 bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] rounded-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <div className="relative z-10 flex flex-col items-center text-center">
                                <Icon className={`w-5 h-5 mb-1 ${isActive ? 'text-white' : 'text-[#4ABDBD]'}`} data-testid={`reports-doctor-status-card-icon-${card.id}`} />
                                <p className="text-[10px] font-medium leading-tight" data-testid={`reports-doctor-status-card-label-${card.id}`}>{card.label}</p>
                                {countsLoading ? (
                                    <div className="mt-0.5 flex items-center justify-center" data-testid={`reports-doctor-status-card-loading-${card.id}`}>
                                        <div className={`w-3 h-3 border-2 border-t-transparent border-current rounded-full animate-spin ${isActive ? 'text-white' : 'text-gray-400'}`}></div>
                                    </div>
                                ) : (
                                    <p className={`text-lg font-bold mt-0.5 ${isActive ? 'text-white' : 'text-gray-800'}`} data-testid={`reports-doctor-status-card-count-${card.id}`}>
                                        {count}
                                    </p>
                                )}
                            </div>
                        </motion.button>
                    );
                })}
            </div>

            {/* Loading State */}
            {loading && (
                <div className="flex flex-col items-center justify-center py-4 pt-12" data-testid="reports-doctor-status-loading-container">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]" data-testid="reports-doctor-status-loading-text">{t('reportsDoctorStatus.loading')}</span>
                </div>
            )}

            {/* Desktop Table - EXACT same as ReportsDoctors */}
            {!loading && (
                <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200" data-testid="reports-doctor-status-table-container">
                    <table className="w-full border-collapse rounded-md overflow-hidden" data-testid="reports-doctor-status-table">
                        <thead data-testid="reports-doctor-status-table-head">
                            <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white" data-testid="reports-doctor-status-table-header-row">
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="reports-doctor-status-header-serial">{t('reportsDoctorStatus.serial_no')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="reports-doctor-status-header-doctor-id">{t('reportsDoctorStatus.doctor_id')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="reports-doctor-status-header-name">{t('reportsDoctorStatus.name')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="reports-doctor-status-header-email">{t('reportsDoctorStatus.email')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="reports-doctor-status-header-phone">{t('reportsDoctorStatus.phone')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="reports-doctor-status-header-license">{t('reportsDoctorStatus.license_no')}</th>
                                {isPendingProfileCard() ? (
                                    <>
                                        {getPendingTableColumns().includes('profile') && (
                                            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="reports-doctor-status-header-profile">{t('reportsDoctorStatus.profile')}</th>
                                        )}
                                        {getPendingTableColumns().includes('speciality') && (
                                            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="reports-doctor-status-header-speciality">{t('reportsDoctorStatus.speciality')}</th>
                                        )}
                                        {getPendingTableColumns().includes('address') && (
                                            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="reports-doctor-status-header-address">{t('reportsDoctorStatus.address')}</th>
                                        )}
                                        {getPendingTableColumns().includes('availability') && (
                                            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="reports-doctor-status-header-availability">{t('reportsDoctorStatus.availability')}</th>
                                        )}
                                        {getPendingTableColumns().includes('payment') && (
                                            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="reports-doctor-status-header-payment">{t('reportsDoctorStatus.payment', 'Payment & Bank')}</th>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="reports-doctor-status-header-gender">{t('reportsDoctorStatus.gender')}</th>
                                        <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="reports-doctor-status-header-created-at">{t('reportsDoctorStatus.created_at')}</th>
                                    </>
                                )}
                                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="reports-doctor-status-header-action">{t('reportsDoctorStatus.action')}</th>
                            </tr>
                        </thead>
                        <tbody data-testid="reports-doctor-status-table-body">
                            {currentDoctors.length === 0 ? (
                                <tr data-testid="reports-doctor-status-empty-row">
                                    <td colSpan={isPendingProfileCard() ? (6 + getPendingTableColumns().length + 1) : "9"} className="p-8 text-center text-gray-500" data-testid="reports-doctor-status-no-data-text">
                                        {t('reportsDoctorStatus.no_data')}
                                    </td>
                                </tr>
                            ) : (
                                currentDoctors.map((doc, index) => {
                                    const serialNo = indexOfFirstDoctor + index + 1;
                                    const fullName = `${doc.name || ''} ${doc.surname || ''}`.trim();
                                    const formattedCreatedAt = formatDate(doc.createdAt);
                                    return (
                                        <tr
                                            key={doc._id || doc.id || `doctor-${index}`}
                                            className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group"
                                            data-testid={`reports-doctor-status-row-${index}`}
                                        >
                                            <td className="px-4 py-5 text-xs text-gray-600 group-hover:text-gray-700" data-testid={`reports-doctor-status-serial-${index}`}>{serialNo}</td>
                                            <td className="px-4 py-5 text-xs text-gray-600 group-hover:text-gray-700" data-testid={`reports-doctor-status-doctor-id-${index}`}>{doc.doctorId || '-'}</td>
                                            <td className="px-4 py-5 text-xs font-medium text-gray-800 group-hover:text-gray-900" data-testid={`reports-doctor-status-name-${index}`}>{fullName || '-'}</td>
                                            <td className="px-4 py-5 text-xs text-gray-600" data-testid={`reports-doctor-status-email-${index}`}>{doc.email || '-'}</td>
                                            <td className="px-4 py-5 text-xs text-gray-600" data-testid={`reports-doctor-status-phone-${index}`}>{doc.phone || '-'}</td>
                                            <td className="px-4 py-5 text-xs text-gray-600" data-testid={`reports-doctor-status-license-${index}`}>{doc.licenseNumber || '-'}</td>
                                            {isPendingProfileCard() ? (
                                                <>
                                                    {getPendingTableColumns().includes('profile') && (
                                                        <td className="px-4 py-5 text-center" data-testid={`reports-doctor-status-profile-${index}`}>
                                                            {doc.profileStatus ? (
                                                                <Check className="w-5 h-5 text-green-600 mx-auto" data-testid={`reports-doctor-status-profile-check-${index}`} />
                                                            ) : (
                                                                <X className="w-5 h-5 text-red-600 mx-auto" data-testid={`reports-doctor-status-profile-cross-${index}`} />
                                                            )}
                                                        </td>
                                                    )}
                                                    {getPendingTableColumns().includes('speciality') && (
                                                        <td className="px-4 py-5 text-center" data-testid={`reports-doctor-status-speciality-${index}`}>
                                                            {doc.appointmentSpeciality ? (
                                                                <Check className="w-5 h-5 text-green-600 mx-auto" data-testid={`reports-doctor-status-speciality-check-${index}`} />
                                                            ) : (
                                                                <X className="w-5 h-5 text-red-600 mx-auto" data-testid={`reports-doctor-status-speciality-cross-${index}`} />
                                                            )}
                                                        </td>
                                                    )}
                                                    {getPendingTableColumns().includes('address') && (
                                                        <td className="px-4 py-5 text-center" data-testid={`reports-doctor-status-address-${index}`}>
                                                            {doc.address ? (
                                                                <Check className="w-5 h-5 text-green-600 mx-auto" data-testid={`reports-doctor-status-address-check-${index}`} />
                                                            ) : (
                                                                <X className="w-5 h-5 text-red-600 mx-auto" data-testid={`reports-doctor-status-address-cross-${index}`} />
                                                            )}
                                                        </td>
                                                    )}
                                                    {getPendingTableColumns().includes('availability') && (
                                                        <td className="px-4 py-5 text-center" data-testid={`reports-doctor-status-availability-${index}`}>
                                                            {doc.availability ? (
                                                                <Check className="w-5 h-5 text-green-600 mx-auto" data-testid={`reports-doctor-status-availability-check-${index}`} />
                                                            ) : (
                                                                <X className="w-5 h-5 text-red-600 mx-auto" data-testid={`reports-doctor-status-availability-cross-${index}`} />
                                                            )}
                                                        </td>
                                                    )}
                                                    {getPendingTableColumns().includes('payment') && (
                                                        <td className="px-4 py-5 text-center" data-testid={`reports-doctor-status-payment-${index}`}>
                                                            {doc.payment ? (
                                                                <Check className="w-5 h-5 text-green-600 mx-auto" data-testid={`reports-doctor-status-payment-check-${index}`} />
                                                            ) : (
                                                                <X className="w-5 h-5 text-red-600 mx-auto" data-testid={`reports-doctor-status-payment-cross-${index}`} />
                                                            )}
                                                        </td>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <td className="px-4 py-5 text-center text-xs text-gray-600 capitalize" data-testid={`reports-doctor-status-gender-${index}`}>{doc.gender ? t(`common.gender.${doc.gender.toLowerCase()}`) : '-'}</td>
                                                    <td className="px-4 py-5 text-center text-xs text-gray-600" data-testid={`reports-doctor-status-created-at-${index}`}>{formattedCreatedAt}</td>
                                                </>
                                            )}
                                            <td className="px-4 py-5 text-center" data-testid={`reports-doctor-status-action-cell-${index}`}>
                                                <button
                                                    onClick={() => handleViewDoctor(doc)}
                                                    className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden inline-flex items-center justify-center"
                                                    data-testid={`reports-doctor-status-view-button-${index}`}
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                    <Eye className="h-4 w-4 relative z-10" data-testid={`reports-doctor-status-view-icon-${index}`} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Mobile View - Cards */}
            {!loading && (
                <div className="block sm:hidden space-y-4" data-testid="reports-doctor-status-mobile-container">
                    {currentDoctors.length === 0 ? (
                        <div className="p-8 text-center text-gray-500" data-testid="reports-doctor-status-mobile-no-data">
                            {t('reportsDoctorStatus.no_data')}
                        </div>
                    ) : (
                        currentDoctors.map((doc, index) => {
                            const serialNo = indexOfFirstDoctor + index + 1;
                            const fullName = `${doc.name || ''} ${doc.surname || ''}`.trim();
                            const formattedCreatedAt = formatDate(doc.createdAt);
                            return (
                                <div key={doc._id || doc.id || `doctor-mobile-${index}`} className="p-4 bg-gray-50 rounded-xl border border-gray-200" data-testid={`reports-doctor-status-mobile-card-${index}`}>
                                    <div className="flex justify-between items-start mb-2">
                                        <span className="text-xs font-bold text-[#4ABDBD]" data-testid={`reports-doctor-status-mobile-serial-${index}`}>{t('reportsDoctorStatus.serial_no')}: {serialNo}</span>
                                        <button
                                            onClick={() => handleViewDoctor(doc)}
                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                            data-testid={`reports-doctor-status-mobile-view-button-${index}`}
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                            <Eye className="h-4 w-4 relative z-10" data-testid={`reports-doctor-status-mobile-view-icon-${index}`} />
                                        </button>
                                    </div>
                                    <div className="text-sm font-medium mb-2" data-testid={`reports-doctor-status-mobile-name-${index}`}>{fullName || '-'}</div>
                                    <div className="text-xs text-gray-600" data-testid={`reports-doctor-status-mobile-doctor-id-${index}`}>{t('reportsDoctorStatus.doctor_id')}: {doc.doctorId || '-'}</div>
                                    <div className="text-xs text-gray-600" data-testid={`reports-doctor-status-mobile-email-${index}`}>{t('reportsDoctorStatus.email_label')}: {doc.email || '-'}</div>
                                    <div className="text-xs text-gray-600" data-testid={`reports-doctor-status-mobile-phone-${index}`}>{t('reportsDoctorStatus.phone_label')}: {doc.phone || '-'}</div>
                                    <div className="text-xs text-gray-600" data-testid={`reports-doctor-status-mobile-license-${index}`}>{t('reportsDoctorStatus.license_label')}: {doc.licenseNumber || '-'}</div>
                                    {isPendingProfileCard() ? (
                                        <>
                                            {getPendingTableColumns().includes('profile') && (
                                                <div className="flex items-center gap-2 text-xs text-gray-600 mt-2" data-testid={`reports-doctor-status-mobile-profile-${index}`}>
                                                    <span className="font-medium">{t('reportsDoctorStatus.profile')}:</span>
                                                    {doc.profileStatus ? (
                                                        <Check className="w-4 h-4 text-green-600" data-testid={`reports-doctor-status-mobile-profile-check-${index}`} />
                                                    ) : (
                                                        <X className="w-4 h-4 text-red-600" data-testid={`reports-doctor-status-mobile-profile-cross-${index}`} />
                                                    )}
                                                </div>
                                            )}
                                            {getPendingTableColumns().includes('speciality') && (
                                                <div className="flex items-center gap-2 text-xs text-gray-600" data-testid={`reports-doctor-status-mobile-speciality-${index}`}>
                                                    <span className="font-medium">{t('reportsDoctorStatus.speciality_label')}:</span>
                                                    {doc.appointmentSpeciality ? (
                                                        <Check className="w-4 h-4 text-green-600" data-testid={`reports-doctor-status-mobile-speciality-check-${index}`} />
                                                    ) : (
                                                        <X className="w-4 h-4 text-red-600" data-testid={`reports-doctor-status-mobile-speciality-cross-${index}`} />
                                                    )}
                                                </div>
                                            )}
                                            {getPendingTableColumns().includes('address') && (
                                                <div className="flex items-center gap-2 text-xs text-gray-600" data-testid={`reports-doctor-status-mobile-address-${index}`}>
                                                    <span className="font-medium">{t('reportsDoctorStatus.address_label')}:</span>
                                                    {doc.address ? (
                                                        <Check className="w-4 h-4 text-green-600" data-testid={`reports-doctor-status-mobile-address-check-${index}`} />
                                                    ) : (
                                                        <X className="w-4 h-4 text-red-600" data-testid={`reports-doctor-status-mobile-address-cross-${index}`} />
                                                    )}
                                                </div>
                                            )}
                                            {getPendingTableColumns().includes('availability') && (
                                                <div className="flex items-center gap-2 text-xs text-gray-600" data-testid={`reports-doctor-status-mobile-availability-${index}`}>
                                                    <span className="font-medium">{t('reportsDoctorStatus.availability_label')}:</span>
                                                    {doc.availability ? (
                                                        <Check className="w-4 h-4 text-green-600" data-testid={`reports-doctor-status-mobile-availability-check-${index}`} />
                                                    ) : (
                                                        <X className="w-4 h-4 text-red-600" data-testid={`reports-doctor-status-mobile-availability-cross-${index}`} />
                                                    )}
                                                </div>
                                            )}
                                            {getPendingTableColumns().includes('payment') && (
                                                <div className="flex items-center gap-2 text-xs text-gray-600" data-testid={`reports-doctor-status-mobile-payment-${index}`}>
                                                    <span className="font-medium">{t('reportsDoctorStatus.payment', 'Payment & Bank')}:</span>
                                                    {doc.payment ? (
                                                        <Check className="w-4 h-4 text-green-600" data-testid={`reports-doctor-status-mobile-payment-check-${index}`} />
                                                    ) : (
                                                        <X className="w-4 h-4 text-red-600" data-testid={`reports-doctor-status-mobile-payment-cross-${index}`} />
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <div className="text-xs text-gray-600 capitalize" data-testid={`reports-doctor-status-mobile-gender-${index}`}>{t('reportsDoctorStatus.gender_label')}: {doc.gender ? t(`common.gender.${doc.gender.toLowerCase()}`) : '-'}</div>
                                            <div className="text-xs text-gray-600" data-testid={`reports-doctor-status-mobile-created-at-${index}`}>{t('reportsDoctorStatus.created_at_label')}: {formattedCreatedAt}</div>
                                        </>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {/* Pagination */}
            {!loading && totalItems > 0 && (
                <div className="" data-testid="reports-doctor-status-pagination-container">
                    <Pagination
                        totalItems={totalItems}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                        custombutton={currentDoctors.length === 0 ? 'disable' : ''}
                    />
                </div>
            )}

            {/* Doctor Profile Modal */}
            <DoctorProfileModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                }}
                doctor={selectedDoctor}
                loading={doctorLoading}
            />
        </motion.div>
    );
};

export default ReportsDoctorStatus;